/**
 * The landing page FAQ as data. The page renders it, and the SEO work builds
 * the FAQPage JSON-LD from the same entries, so both always say the same.
 *
 * Answers are plain text (no markup, no links) and every claim must hold in
 * the code today. The pricing answer reads its numbers from the plan config.
 */
import { PLANS, formatCzk } from "@/lib/pricing/plans";
import type { Localized } from "./locale";

export interface FaqItem {
  /** Stable id, used as the anchor (`#faq-<id>`) and as a React key. */
  id: string;
  question: Localized;
  answer: Localized;
}

const [start, firma] = PLANS;
const trialDays = start.trialDays ?? 0;

export const FAQ_ITEMS: readonly FaqItem[] = [
  {
    id: "pripojeni-fio",
    question: {
      cs: "Jak napojit Fio banku na Zettl?",
      en: "How do I connect Fio banka to Zettl?",
    },
    answer: {
      cs: "Ve Fio internetbankingu si vytvoříte API token pro sledování účtu a vložíte ho do Zettlu v Nastavení → Banky a integrace. Od té chvíle Zettl stahuje platby z vašeho účtu každý den sám a páruje je s doklady. Heslo do banky Zettl nepotřebuje.",
      en: "Create an account-monitoring API token in Fio internet banking and paste it into Zettl under Settings → Banks & integrations. From then on Zettl downloads the payments from your account every day on its own and matches them with your documents. Zettl never needs your banking password.",
    },
  },
  {
    id: "api-token",
    question: {
      cs: "Jak vytvořit API token ve Fio bance a jaká práva má mít?",
      en: "How do I create an API token at Fio banka, and which permission does it need?",
    },
    answer: {
      cs: "V internetovém bankovnictví Fio otevřete Nastavení → API a vytvořte nový token s oprávněním „Sledování účtu“. Právo zadávat platby nepotřebuje. Platnost tokenu nastavíte při jeho vytvoření; zapněte prodlužování platnosti při přihlášení, pak ji každé přihlášení do internetového bankovnictví prodlouží. Pokud účet podepisuje více osob, musí token podepsat všechny. Fio vás požádá o potvrzení (SMS nebo push) a token začne fungovat zhruba po pěti minutách. Pro starší pohyby než 90 dní token ve Fio krátce odemknete; Fio je pak zpřístupní na 10 minut.",
      en: "In Fio internet banking, open Settings → API and create a new token with the “Account monitoring” permission. It does not need the right to send payments. You set the token’s validity when you create it; turn on extending the validity at login and every login to Fio internet banking extends it. If several people sign for the account, all of them must sign the token. Fio asks you to confirm the token by SMS or push notification, and it works about five minutes later. For transactions older than 90 days you briefly unlock the token at Fio, which then opens them for 10 minutes.",
    },
  },
  {
    id: "vic-uctu",
    question: {
      cs: "Mám u Fio korunový i eurový účet. Jde to?",
      en: "I have a CZK and a EUR account at Fio. Does that work?",
    },
    answer: {
      cs: "Ano. Token u Fio platí vždy jen pro jeden účet, takže korunový a eurový účet přidáte zvlášť a Zettl je stahuje oba. Kolik účtů můžete připojit, určuje tarif.",
      en: "Yes. A Fio token always covers exactly one account, so you add the CZK and the EUR account separately and Zettl syncs both. Your plan sets how many accounts you can connect.",
    },
  },
  {
    id: "heslo-do-banky",
    question: {
      cs: "Může Zettl z mého účtu posílat platby?",
      en: "Can Zettl send payments from my account?",
    },
    answer: {
      cs: "Ne. Zettl potřebuje jen token pro sledování účtu a volá jen ta rozhraní Fio API, která čtou pohyby a výpisy. Funkci pro zadání platby nemá. Vaše přihlašovací údaje do Fio nikdy nevidí a token ve Fio kdykoli zrušíte. Ukládáme ho šifrovaný (AES-GCM).",
      en: "No. Zettl only needs an account-monitoring token and only calls the parts of the Fio API that read transactions and statements. It has no function for sending payments. It never sees your Fio login, and you can revoke the token in Fio at any time. We store it encrypted (AES-GCM).",
    },
  },
  {
    id: "odkud-doklady",
    question: {
      cs: "Jak se doklady dostanou do Zettlu?",
      en: "How do my receipts get into Zettl?",
    },
    answer: {
      cs: "Na e-mailovou adresu, kterou Zettl přidělí vaší firmě, přepošlete e-mail s účtenkou nebo fakturou v příloze (PDF, JPG, PNG), nebo doklad nahrajete přímo. AI z nich přečte dodavatele, datum a částku a Zettl je spáruje s platbou. Každé párování zkontrolujete a jedním klikem zrušíte; co se nespáruje samo, přiřadíte ručně.",
      en: "Forward the e-mail with the receipt or invoice attached (PDF, JPG, PNG) to the address Zettl gives your company, or upload the document directly. The AI reads the supplier, date and amount, and Zettl matches the document to the payment. You check every match and can undo it with one click; anything that does not match on its own you assign by hand.",
    },
  },
  {
    id: "podklady-ucetni",
    question: {
      cs: "Jak dostanu podklady k účetní?",
      en: "How do I get everything to my accountant?",
    },
    answer: {
      cs: "V přehledu měsíce vidíte, ke kterým platbám doklad ještě chybí. Když je hotovo, stáhnete účtenky za měsíc jako jeden PDF balíček s přehledem, vydané faktury vyexportujete do XLSX nebo ISDOC, platby do CSV a výpis z Fio uložíte jako PDF. Účetní s vlastním přístupem do Zettlu můžete také přizvat do své firmy; jako člen týmu pak doklady může i upravovat.",
      en: "The month view shows which payments still lack a document. Once it is complete, you download the month's receipts as one PDF bundle with a summary, export issued invoices to XLSX or ISDOC and payments to CSV, and save the Fio statement as a PDF. If your accountant has their own Zettl access, you can also add them to your company; as a team member they can edit documents too.",
    },
  },
  {
    id: "ai-asistent-mcp",
    question: {
      cs: "Funguje Zettl s Claude nebo jiným AI asistentem?",
      en: "Does Zettl work with Claude or another AI assistant?",
    },
    answer: {
      cs: "Ano, v tarifu Firma. Zettl má vzdálený MCP server na adrese https://zettlapp.com/mcp. V AI asistentovi s podporou vzdálených MCP konektorů, např. Claude, ho přidáte jako konektor, přihlásíte se účtem Zettl (OAuth 2.1) a na souhlasové obrazovce potvrdíte přístup k jedné firmě: jen ke čtení, nebo i ke změnám (párování dokladů, kategorie plateb, stav faktur). Asistent vám pak řekne třeba to, které platby ještě nemají doklad. Připojení kdykoli odpojíte v Nastavení → Banky a integrace → AI připojení a odpojení platí okamžitě.",
      en: "Yes, in the Firma plan. Zettl runs a remote MCP server at https://zettlapp.com/mcp. In an AI assistant that supports remote MCP connectors, such as Claude, you add it as a connector, sign in with your Zettl account (OAuth 2.1) and confirm access to one company on the consent screen: read only, or also changes (matching documents, payment categories, invoice status). The assistant can then tell you, for example, which payments still have no document. You can disconnect at any time under Settings → Banks & integrations → AI connections, and it takes effect immediately.",
    },
  },
  {
    id: "osvc",
    question: {
      cs: "Je Zettl vhodný pro OSVČ?",
      en: "Is Zettl a good fit for sole traders (OSVČ)?",
    },
    answer: {
      cs: "Ano. Zettl potřebuje jen účet u Fio banky a je jedno, jestli podnikáte jako OSVČ, nebo s.r.o. Pro jednoho podnikatele je tarif Start. Osobní pravidelné výdaje si v soukromém režimu rozpočtujete odděleně od firmy.",
      en: "Yes. Zettl only needs an account at Fio banka; it does not matter whether you are a sole trader or a limited company. The Start plan is made for one-person businesses. In private mode you budget your personal recurring expenses apart from the company.",
    },
  },
  {
    id: "gpc-abo",
    question: {
      cs: "Co je GPC/ABO a k čemu ho potřebuji?",
      en: "What is GPC/ABO and why would I need it?",
    },
    answer: {
      cs: "GPC (ABO) je formát elektronického výpisu, který si ve Fio stáhnete. Přes API vydá Fio bez další autorizace jen posledních 90 dní, takže starší pohyby do Zettlu nahrajete jako výpis GPC (ABO). U výpisu GPC Zettl pozná platby, které už stáhl přes API, a nezdvojí je.",
      en: "GPC (ABO) is an electronic statement format you can download from Fio. Through the API, Fio only releases the last 90 days without extra authorization, so you upload older transactions as a GPC (ABO) statement. With a GPC statement, Zettl recognises payments it already downloaded via the API and does not duplicate them.",
    },
  },
  {
    id: "jina-banka",
    question: {
      cs: "Funguje Zettl i s jinou bankou než Fio?",
      en: "Does Zettl work with banks other than Fio banka?",
    },
    answer: {
      cs: "Zettl je určený pro klienty Fio banky: automatické stahování plateb funguje jen s ní. Pokud účet u Fio nemáte, Zettl vám dnes nepomůže tak, jak by měl.",
      en: "Zettl is built for Fio banka: automatic payment download only works with Fio banka. If you don't have an account there, Zettl won't do much for you today.",
    },
  },
  {
    id: "cena-dph",
    question: {
      cs: "Kolik Zettl stojí?",
      en: "How much does Zettl cost?",
    },
    answer: {
      cs: `${start.name} (párování plateb) stojí ${formatCzk(start.priceMonthlyCzk)} měsíčně, ${firma.name} (celá sada včetně fakturace a více firem) ${formatCzk(firma.priceMonthlyCzk)} měsíčně. Nejsme plátci DPH, uvedená cena je konečná. Na začátku máte prvních ${trialDays} dní zdarma, bez platební karty.`,
      en: `${start.name} (payment matching) costs ${formatCzk(start.priceMonthlyCzk)} a month, ${firma.name} (the whole suite, invoicing and several companies included) ${formatCzk(firma.priceMonthlyCzk)} a month. We are not VAT registered, so the price shown is final. The first ${trialDays} days are free, with no card needed.`,
    },
  },
];
